import React, { useState, useImperativeHandle, forwardRef } from 'react';
import ContextHelp from '../../ContextHelp/ContextHelp';
import AppModal from '../../../components/common/AppModal';
import {
  ddToDmm,
  ddToDms,
  parseDmmToDd,
  parseDmsToDd,
  toGeoJsonPoint,
} from './latLongConversions';


import './LatLngConverter.scss';
interface LatLngConverterProps {
  isOpen: boolean;
  onClose: () => void;
}

const LatLngConverter = forwardRef<{ clearResults: () => void }, LatLngConverterProps>(({ isOpen, onClose }, ref) => {
  const [latitude, setLatitude] = useState<string>('');
  const [longitude, setLongitude] = useState<string>('');
  const [format, setFormat] = useState<string>('DD');
  const [convertedResults, setConvertedResults] = useState<string>('');
  const [error, setError] = useState<string>('');

  useImperativeHandle(ref, () => ({
    clearResults() {
      setConvertedResults('');
      setError('');
    }
  }));

  const handleFormatChange = (newFormat: string) => {
    setFormat(newFormat);
    setError('');
  };

  const handleConvert = () => {
    setError('');

    if (!format) {
      setError('Please choose a target format.');
      return;
    }
    if (!latitude.trim() || !longitude.trim()) {
      setError('Please enter both latitude and longitude.');
      return;
    }

    let results: Record<string, string> = {};

    switch (format) {
      case 'DD':
        results = convertFromDD(latitude, longitude);
        break;
      case 'DMM':
        results = convertFromDMM(latitude, longitude);
        break;
      case 'DMS':
        results = convertFromDMS(latitude, longitude);
        break;
    }

    const formattedResults = formatResults(results);

    if (formattedResults.includes('NaN')) {
      setError(`Could not parse the input. Expected format example — Lat: ${getLatitudePlaceholder()}; Lon: ${getLongitudePlaceholder()}`);
      setConvertedResults('');
      return;
    }

    setConvertedResults(formattedResults);
  };

  const formatResults = (results: any) => {
    return Object.entries(results)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
  };

  const convertFromDD = (lat: string, lon: string) => {
    const latDD = parseFloat(lat);
    const lonDD = parseFloat(lon);
    return {
      'DDM': `${ddToDmm(latDD)}, ${ddToDmm(lonDD)}`,
      'DMS': `${ddToDms(latDD)}, ${ddToDms(lonDD)}`,
      GeoJSON: toGeoJsonPoint(latDD, lonDD),
    };
  };

  const convertFromDMM = (lat: string, lon: string) => {
    const latDD = parseDmmToDd(lat);
    const lonDD = parseDmmToDd(lon);
    return {
      'DD': `${latDD}, ${lonDD}`,
      'DMS': `${ddToDms(latDD)}, ${ddToDms(lonDD)}`,
      GeoJSON: toGeoJsonPoint(latDD, lonDD),
    };
  };

  const convertFromDMS = (lat: string, lon: string) => {
    const latDD = parseDmsToDd(lat);
    const lonDD = parseDmsToDd(lon);
    return {
      'DD': `${latDD}, ${lonDD}`,
      'DDM': `${ddToDmm(latDD)}, ${ddToDmm(lonDD)}`,
      GeoJSON: toGeoJsonPoint(latDD, lonDD),
    };
  };

  const getLatitudeLabel = () => {
    switch (format) {
      case 'DD':
        return 'Latitude (Decimal Degrees):';
      case 'DMM':
        return 'Latitude (Degrees and Decimal Minutes):';
      case 'DMS':
        return 'Latitude (Degrees, Minutes, and Seconds):';
      default:
        return 'Latitude:';
    }
  };

  const getLongitudeLabel = () => {
    switch (format) {
      case 'DD':
        return 'Longitude (Decimal Degrees):';
      case 'DMM':
        return 'Longitude (Degrees and Decimal Minutes):';
      case 'DMS':
        return 'Longitude (Degrees, Minutes, and Seconds):';
      default:
        return 'Longitude:';
    }
  };
  const getLongitudePlaceholder = () => {
    switch (format) {
      case 'DD':
        return 'eg: -111.123';
      case 'DMM':
        return 'eg: -111 7.38 or W111 7.38';
      case 'DMS':
        return 'eg: -111 7 3.8 or W111 7 3.8';
      default:
        return 'Longitude:';
    }
  };


  const getLatitudePlaceholder = () => {
    switch (format) {
      case 'DD':
        return 'eg: 60.123';
      case 'DMM':
        return 'eg: 60 1.23 or N60 1.23';
      case 'DMS':
        return 'eg: 60 1 2.3 or N60 1 2.3';
      default:
        return 'Latitude:';
    }
  };

  const getInstructionTargets = (fmt: string): string[] => {
    switch (fmt) {
      case 'DD':
        return ['DDM', 'DMS', 'GeoJSON'];
      case 'DMM':
        return ['DD', 'DMS', 'GeoJSON'];
      case 'DMS':
        return ['DD', 'DDM', 'GeoJSON'];
      default:
        return ['DD', 'DDM', 'DMS', 'GeoJSON'];
    }
  };




  return (
    <AppModal isOpen={isOpen} onClose={onClose} width={520} maxHeight="85vh">
      <div className="llc-content">
        <div className="modal-header">
          <ContextHelp title='Help with Lat Long Converter' context='llc-help' />
        </div>

        <h2>Lat Long Converter</h2>
        <div>
          <h4>Convert from?</h4>
          <label>
            <input
              type="radio"
              value="DD"
              checked={format === 'DD'}
              onChange={(e) => handleFormatChange(e.target.value)}
            />
            Decimal Degrees (DD)<br />
          </label>
          <label>
            <input
              type="radio"
              value="DMM"
              checked={format === 'DMM'}
              onChange={(e) => handleFormatChange(e.target.value)}
            />
            Degrees and Decimal Minutes (DDM)<br />
          </label>
          <label>
            <input
              type="radio"
              value="DMS"
              checked={format === 'DMS'}
              onChange={(e) => handleFormatChange(e.target.value)}
            />
            Degrees, Minutes, and Decimal Seconds (DMS)<br /><br />
          </label>
        </div>
        <div>
          <label>
            {getLatitudeLabel()}<br />
            <input
              type="text"
              size={30}
              placeholder={getLatitudePlaceholder()}
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
            />
          </label>
        </div>
        <div>
          <label>
            {getLongitudeLabel()}<br />
            <input
              type="text"
              size={30}
              value={longitude}
              placeholder={getLongitudePlaceholder()}
              onChange={(e) => setLongitude(e.target.value)}
            />
          </label>
        </div><br />
        <button onClick={handleConvert}>Convert</button><br />
        {error && (
          <div className="llc-error" role="alert">{error}</div>
        )}
        <div>


          <div className='llc-instructions'>
            This can convert to:
            <ul>
              {getInstructionTargets(format).map((target) => (
                <li key={target}>{target}</li>
              ))}
            </ul>
          </div>

          <label>
            Converted Results:<br />

            <textarea
              value={convertedResults}
              readOnly
              rows={10}
              cols={40}
            />
          </label>
        </div>
      </div>
    </AppModal>
  );
});

export default LatLngConverter;